import React, { useMemo } from 'react';
import { TreeSelect } from 'antd';

interface CategoryNode {
  id: number;
  name: string;
  code?: string;
  fullPath?: string;
  parentId?: number | null;
  level?: number;
  isActive?: boolean;
  children?: CategoryNode[];
}

interface CategoryTreeSelectProps {
  value?: number | number[];
  onChange?: (value: any) => void;
  categoryTree: CategoryNode[];
  categories?: CategoryNode[]; // flat list fallback
  allowClear?: boolean;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  size?: 'small' | 'middle' | 'large';
  loading?: boolean;
  multiple?: boolean;
}

function buildTreeData(nodes: CategoryNode[]): any[] {
  return nodes
    .filter((n) => n.isActive !== false)
    .map((node) => ({
      value: node.id,
      title: node.name,
      fullPath: node.fullPath || node.name,
      children: node.children && node.children.length > 0 ? buildTreeData(node.children) : undefined,
    }));
}

// Fallback: build tree from flat list
function buildTreeFromFlat(flat: CategoryNode[]): any[] {
  const active = flat.filter((n) => n.isActive !== false);
  const map = new Map<number, any>();
  const roots: any[] = [];

  for (const node of active) {
    map.set(node.id, {
      value: node.id,
      title: node.name,
      fullPath: node.fullPath || node.name,
      children: [],
    });
  }

  for (const node of active) {
    const treeNode = map.get(node.id)!;
    if (node.parentId && map.has(node.parentId)) {
      map.get(node.parentId)!.children.push(treeNode);
    } else {
      roots.push(treeNode);
    }
  }

  // Clean up empty children arrays
  function cleanEmpty(nodes: any[]) {
    for (const n of nodes) {
      if (n.children.length === 0) delete n.children;
      else cleanEmpty(n.children);
    }
  }
  cleanEmpty(roots);

  return roots;
}

const CategoryTreeSelect: React.FC<CategoryTreeSelectProps> = ({
  value,
  onChange,
  categoryTree,
  categories,
  allowClear = true,
  placeholder = 'Select category',
  disabled = false,
  style,
  size,
  loading,
  multiple = false,
}) => {
  const treeData = useMemo(() => {
    if (categoryTree && categoryTree.length > 0) {
      return buildTreeData(categoryTree);
    }
    if (categories && categories.length > 0) {
      return buildTreeFromFlat(categories);
    }
    return [];
  }, [categoryTree, categories]);

  return (
    <TreeSelect
      value={value}
      onChange={onChange}
      treeData={treeData}
      allowClear={allowClear}
      showSearch
      treeNodeFilterProp="title"
      placeholder={placeholder}
      disabled={disabled}
      style={style || { width: '100%' }}
      size={size}
      loading={loading}
      multiple={multiple}
      treeLine={{ showLeafIcon: false }}
      treeDefaultExpandAll={treeData.length < 50}
      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
    />
  );
};

export default CategoryTreeSelect;
